import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import { Observable, of, merge } from 'rxjs';
import { FieldTaskDTO } from '../../../../../harvest-engine/modules/field-events/models/field-task';


/**
 * Data source for the BlocksTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class FieldTaskTableDataSource extends DataSource<FieldTaskDTO> {
  data: FieldTaskDTO[] = [];

  constructor(private paginator: MatPaginator, private sort: MatSort) {
    super();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<FieldTaskDTO[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations : any = [
      of(this.data),
      this.paginator.page,
      this.sort.sortChange
    ];

    // Set the paginator's length
    this.paginator.length = this.data.length;

    return merge(...dataMutations).pipe(map(() => {
      return this.getPagedData(this.getSortedData([...this.data]));
    }));
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() {

  }

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: FieldTaskDTO[]) {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.splice(startIndex, this.paginator.pageSize);
  }

  public setPaginator(length: number){
    this.paginator._changePageSize(length);
    this.paginator.pageSize = length;
    
  }
  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: FieldTaskDTO[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        // case 'fieldTaskId': return compare(a.fieldTaskId, b.fieldTaskId, isAsc);
        case 'startDate': return compare(a.startDate, b.startDate, isAsc);
        case 'dueDate': return compare(a.dueDate, b.dueDate, isAsc);
        case 'assignedUsers': return compare(a.assignedUsers, b.assignedUsers, isAsc);
        // case 'requestedFarms': return compare(a.requestedFarms, b.requestedFarms, isAsc);
        case 'requestedBlocks': return compare (a.requestedBlocks.length, b.requestedBlocks.length, isAsc);
        case 'requestedFieldEventType': return compare(a.requestedFieldEventType.name, b.requestedFieldEventType.name, isAsc);
        case 'createdBy': return compare(a.createdByUserId, b.createdByUserId, isAsc);
        // case 'fieldEvents': return compare(a.fieldEvents.length, b.fieldEvents.length, isAsc);
        case 'isUserCompleted': return compare(a.isUserCompleted, b.isUserCompleted, isAsc);
        case 'completedBy': return compare(a.completedByUserId, b.completedByUserId, isAsc);
        case 'completedDate': return compare(a.completedDate, b.completedDate, isAsc);
        case 'requestorComments': return compare(a.requestorComments, b.requestorComments, isAsc);

        default: return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a, b, isAsc) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
